<template>
  <v-container>
    <v-expansion-panels variant="accordion" class="mb-4">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <span class="text-h6 font-weight-medium text-primary">Fiche Laverie {{ laverie.numeroDeContrat }}</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-card class="mb-4">
            <v-card-text>
              <v-text-field v-model="laverie.name" label="Nom" density="compact" />
              <v-text-field v-model="laverie.fullAddress" label="Adresse" density="compact" />
              <v-text-field v-model="laverie.telephonePrincipal" label="Contact principal" density="compact" />
              <v-text-field v-model="laverie.telephoneAutre" label="Contact secondaire" density="compact" />
              <v-text-field v-model="laverie.codeAcces" label="Code d'accès" density="compact" />
            </v-card-text>
          </v-card>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <div v-if="!isInterventionStarted" class="d-flex justify-center mb-4">
      <v-btn color="primary" class="text-white" min-width="120" @click="startIntervention()">
        Démarrer l'intervention
      </v-btn>
    </div>

    <!-- ACTIVITE -->
    <v-expansion-panels variant="accordion" class="mb-4">
      <v-expansion-panel :disabled="!isInterventionStarted">
        <v-expansion-panel-title>
          <span class="text-h6 font-weight-medium text-primary">Activité</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-card class="mb-4">
            <v-card-text>
              <v-text-field label="Type d'activité" :items="activityTypes" v-model="task.taskType" />

              <v-select label="Statut" :items="statusItems" v-model="task.taskStatus" />
              <v-textarea label="Description" v-model="task.subject" />
            </v-card-text>
          </v-card>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- PRODTUIS -->
    <v-expansion-panels variant="accordion" class="mb-4">
      <v-expansion-panel :disabled="!isInterventionStarted">
        <v-expansion-panel-title>
          <span class="text-h6 font-weight-medium text-primary">Produits</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-expansion-panels multiple>
            <v-expansion-panel :disabled="!isInterventionStarted" v-for="product in productsWithIntervention"
              :key="product.id">
              <!-- PRODUCT HEADER -->
              <v-expansion-panel-title>
                {{ product.aquaNb }} – {{ product.productType }} –
                {{ product.productMake }}
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <v-tabs v-model="productTabs[product.id]" color="primary" class="mb-4">
                  <v-tab value="intervention">Intervention</v-tab>
                  <v-tab value="pannes">Pannes</v-tab>
                </v-tabs>

                <v-window v-model="productTabs[product.id]">
                  <v-window-item value="intervention">
                    <div v-if="getIntervention(product.id)" class="mb-6 pt-4">
                      <!-- CHECKLIST ITEMS -->
                      <div v-for="item in getIntervention(product.id).items" :key="item.id">
                        <!-- CHECKBOX -->
                        <v-checkbox v-if="item.itemType === 'CHECKBOX'" v-model="item.completed" :label="item.label"
                          hide-details />

                        <!-- NUMERIC -->
                        <v-text-field v-if="item.itemType === 'NUMERIC'" type="number" :label="item.label"
                          v-model="item.numericValue" density="comfortable" />

                        <!-- TEXT -->
                        <v-textarea v-if="item.itemType === 'TEXT'" :label="item.label" v-model="item.textValue"
                          auto-grow />
                      </div>

                      <v-text-field label="Commentaire" v-model="getIntervention(product.id).comment"
                        density="comfortable" />

                      <v-divider class="my-4" />

                      <div class="d-flex justify-center my-3">
                        <v-btn color="primary" class="text-white" min-width="140"
                          @click="completeIntervention(getIntervention(product.id))">
                          Terminer pour ce produit
                        </v-btn>
                      </div>
                    </div>
                  </v-window-item>

                  <v-window-item value="pannes">
                    <!-- PANNES -->
                    <v-expansion-panels variant="accordion" v-model="openedPanneIndex[product.id]">
                      <v-expansion-panel v-for="(panne, i) in pannesPerProduct[product.id] || []" :key="panne.id ?? i"
                        :value="i" :class="{ 'opacity-50': panne.repaired }">
                        <!-- PANNE HEADER -->
                        <v-expansion-panel-title @click="ensurePanneThumbnailsLoaded(panne)">
                          <div class="d-flex align-center justify-space-between w-100">
                            <span>{{ panne.name || "Nouvelle panne" }}</span>
                          </div>
                        </v-expansion-panel-title>

                        <!-- PANNE BODY -->
                        <v-expansion-panel-text>
                          <v-text-field label="Titre" v-model="panne.name" density="compact"
                            :disabled="panne.repaired" />

                          <v-textarea label="Description panne" v-model="panne.desc" :disabled="panne.repaired"
                            auto-grow rows="3" />
                          <div class="d-flex flex-wrap ga-4">
                            <v-checkbox v-model="panne.createTask" label="Créer une activité de panne" density="compact"
                              hide-details />
                            <v-checkbox v-model="panne.repaired" label="Réparée" density="compact" hide-details />
                          </div>
                          <v-divider class="mt-4 mb-6" />

                          <v-row class="mb-2" density="compact">
                            <v-col cols="12" md="6" class="d-flex flex-column align-center">
                              <div class="text-subtitle-2 mb-2">Photo avant réparation</div>
                              <div class="d-flex ga-2 mb-2 justify-center">
                                <v-btn size="small" variant="outlined" color="primary" prepend-icon="mdi-paperclip"
                                  :disabled="panne.repaired || !panne.id"
                                  @click="triggerPanneFileInput(`panne-before-input-${product.id}-${panne.id ?? i}`)">
                                  Attacher
                                </v-btn>
                                <v-btn size="small" variant="outlined" color="primary" prepend-icon="mdi-camera"
                                  :disabled="panne.repaired || !panne.id || !isBrowserCameraAvailable"
                                  @click="openPanneCamera(panne, VaultFileTag.BEFORE)">
                                  Caméra
                                </v-btn>
                              </div>
                              <input :id="`panne-before-input-${product.id}-${panne.id ?? i}`" type="file"
                                accept="image/*" class="d-none"
                                @change="onPanneFileSelected($event, panne, VaultFileTag.BEFORE)" />
                              <div class="panne-thumbnail-wrapper mt-2">
                                <v-img v-if="panne.id && pannePhotoBeforeUrls[panne.id]"
                                  :src="pannePhotoBeforeUrls[panne.id]" class="rounded panne-thumbnail"
                                  @click="openPannePhoto(panne.id, VaultFileTag.BEFORE)" />
                              </div>
                              <v-btn v-if="panne.id && pannePhotoBeforeUrls[panne.id]" size="x-small" variant="text"
                                color="primary" prepend-icon="mdi-download" class="mt-1"
                                @click="downloadPannePhoto(panne.id, VaultFileTag.BEFORE)">
                                Télécharger
                              </v-btn>
                            </v-col>

                            <v-col cols="12" md="6" class="d-flex flex-column align-center">
                              <div class="text-subtitle-2 mb-2">Photo après réparation</div>
                              <div class="d-flex ga-2 mb-2 justify-center">
                                <v-btn size="small" variant="outlined" color="primary" prepend-icon="mdi-paperclip"
                                  :disabled="panne.repaired || !panne.id"
                                  @click="triggerPanneFileInput(`panne-after-input-${product.id}-${panne.id ?? i}`)">
                                  Attacher
                                </v-btn>
                                <v-btn size="small" variant="outlined" color="primary" prepend-icon="mdi-camera"
                                  :disabled="panne.repaired || !panne.id || !isBrowserCameraAvailable"
                                  @click="openPanneCamera(panne, VaultFileTag.AFTER)">
                                  Caméra
                                </v-btn>
                              </div>
                              <input :id="`panne-after-input-${product.id}-${panne.id ?? i}`" type="file"
                                accept="image/*" class="d-none"
                                @change="onPanneFileSelected($event, panne, VaultFileTag.AFTER)" />
                              <div class="panne-thumbnail-wrapper mt-2">
                                <v-img v-if="panne.id && pannePhotoAfterUrls[panne.id]"
                                  :src="pannePhotoAfterUrls[panne.id]" class="rounded panne-thumbnail"
                                  @click="openPannePhoto(panne.id, VaultFileTag.AFTER)" />
                              </div>
                              <v-btn v-if="panne.id && pannePhotoAfterUrls[panne.id]" size="x-small" variant="text"
                                color="primary" prepend-icon="mdi-download" class="mt-1"
                                @click="downloadPannePhoto(panne.id, VaultFileTag.AFTER)">
                                Télécharger
                              </v-btn>
                            </v-col>
                          </v-row>

                          <v-divider class="my-4" />

                          <!-- INSTALLED SPAREPARTS -->
                          <div v-for="(installedSparepart, j) in panne.installedSpareParts || []" :key="j"
                            class="mb-2 d-flex ga-3 align-center">
                            <div style="width: 75%" :class="{
                              'red-outline-wrapper': installedSparepart.sparepartId && !isSparePartAvailable(installedSparepart.sparepartId),
                              'green-outline-wrapper': installedSparepart.sparepartId && isSparePartAvailable(installedSparepart.sparepartId)
                            }">
                              <v-autocomplete :label="getSparePartLabel(installedSparepart.sparepartId)"
                                :items="spareparts" v-model="installedSparepart.sparepartId" item-title="displayName"
                                item-value="id" :disabled="panne.repaired" />
                            </div>

                            <v-text-field style="width: 25%" type="number" label="Quantité"
                              v-model="installedSparepart.quantity" :disabled="panne.repaired" />
                          </div>

                          <!-- ADD INSTALLED SPAREPART -->
                          <div class="d-flex justify-end mt-2">
                            <v-btn size="small" variant="outlined" color="primary" :disabled="panne.repaired"
                              @click="addInstalledSparepart(panne)">
                              Ajouter pièce détachée
                            </v-btn>
                          </div>

                          <div class="d-flex justify-center mt-4">
                            <v-btn color="primary" class="text-white" min-width="120"
                              @click="submitPanne(product, panne)">
                              Soumettre Panne
                            </v-btn>

                            <v-spacer style="max-width: 16px" />

                            <v-btn color="error" class="text-white" min-width="120" @click="removePanne(product, i)">
                              {{ isPendingPanne(panne) ? "Annuler Panne" : "Supprimer Panne" }}
                            </v-btn>
                          </div>
                        </v-expansion-panel-text>
                      </v-expansion-panel>
                    </v-expansion-panels>

                    <div v-if="openedPanneIndex[product.id] == null" class="d-flex justify-center my-3">
                      <v-btn color="primary" class="text-white" min-width="140" @click="addPanne(product)">
                        Ajouter panne
                      </v-btn>
                    </div>
                  </v-window-item>
                </v-window>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- TACHES -->
    <v-expansion-panels variant="accordion" class="mb-4">
      <v-expansion-panel :disabled="!isInterventionStarted">
        <v-expansion-panel-title>
          <span class="text-h6 font-weight-medium text-primary">Tâches</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <SubtaskList :subtasks="task.subtasks || []" :show-title="false" :show-outline="false" @remove="removeSubtask"
            @update="updateSubtask" />
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- OBSERVATION -->
    <v-expansion-panels variant="accordion" class="mb-4">
      <v-expansion-panel :disabled="!isInterventionStarted">
        <v-expansion-panel-title>
          <span class="text-h6 font-weight-medium text-primary">Observation</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-textarea class="mt-4" v-model="task.observation" />
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <div v-if="!isInterventionEnded && isInterventionStarted" class="d-flex justify-center mb-4">
      <v-btn color="primary" class="text-white" min-width="120" @click="endIntervention()">
        Terminer l'intervention
      </v-btn>
    </div>

    <!-- SIGNATURE -->
    <v-expansion-panels :disabled="!isInterventionEnded" variant="accordion" class="mb-4">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <span class="text-h6 font-weight-medium text-primary"> Signature client </span>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <v-card variant="outlined" rounded="lg" class="pa-4">
            <div class="d-flex justify-center mb-4">
              <SignaturePad v-if="!signatureFile" ref="signature" />

              <v-img v-else :src="signatureUrl" max-width="400" max-height="400" />
            </div>

            <div v-if="!signatureFile" class="d-flex justify-end">
              <v-btn variant="outlined" class="mr-2" @click="clearSignature">
                Annuler
              </v-btn>

              <v-btn color="primary" class="text-white" :disabled="showProgressBar" @click="saveSignature">
                Enregistrer
              </v-btn>
            </div>
          </v-card>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- ACTIONS -->
    <v-row v-if="laverie.email" class="mt-6">
      <v-col>
        <v-btn :disabled="!isInterventionEnded" color="success" block @click="sendEmail">
          Envoyer email
        </v-btn>
      </v-col>
    </v-row>
    <UploadProgress :visible="showProgressBar" :file-name="progressInfo.fileName"
      :percentage="progressInfo.percentage" />

    <v-dialog v-model="isPanneCameraDialogOpen" max-width="900">
      <v-card>
        <v-card-title>
          {{ panneCameraTarget?.tag === VaultFileTag.BEFORE ? "Photo avant réparation" : "Photo après réparation" }}
        </v-card-title>
        <v-card-text>
          <CameraComponent v-if="isPanneCameraDialogOpen" :auto-start="true"
            @photo-confirmed="onPanneCameraPhotoConfirmed" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closePanneCamera">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script setup lang="ts">
import { useAppStore, useSessionStore } from "@/store";
import { Intervention, InterventionItemType, Panne, Product, ProgressInfo, RefEntityType, SparePart, Subtask, VaultFileTag, VaultFileType, Vehicle, VehicleInventoryItem } from "@/types";
import { Laverie } from "@/types";
import { Task } from "@/types";
import { fetchFileContent, fetchThumbnail, fetchVaultFiles, uploadPannePhoto, uploadTaskSignature } from "@/utils/vault-file-api";
import { getLaverieById } from "@/utils/laverie-api";
import { getProductsByLaverieId } from "@/utils/product-api";
import { getTaskById, sendReport, setEndDateTime, setStartDateTime } from "@/utils/task-api";
import { AxiosProgressEvent } from "axios";
import { ref, onBeforeUnmount, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import SignaturePad from "vue3-signature";
import { VaultFile } from "@/types/vault-file";
import UploadProgress from "@/components/UploadProgress.vue";
import SubtaskList from "@/components/SubtaskList.vue";
import CameraComponent from "@/components/CameraComponent.vue";
import { fetchInterventionsByTaskId, putIntervention } from "@/utils/intervention-api";
import { deletePanne, getPanneByProduct, postPanne, putPanne } from "@/utils/panne-api";
import { getVehicleByTaskListId, getVehicleInventoryItems } from "@/utils/vehicle-api";
import { getAllSpareparts } from "@/utils/sparepart-api";

const activityTypes = ["Panne", "Maintenance"];
const statusItems = ["En cours", "Terminé", "Reporté"];


const route = useRoute();
const sessionStore = useSessionStore();
const appStore = useAppStore();

const task = ref<Task>({} as Task);
const laverie = ref<Laverie>({} as Laverie);
const vehicle = ref<Vehicle>({} as Vehicle);
const products = ref<Array<Product>>([]);
const spareparts = ref<Array<SparePart>>([]);
const interventions = ref<Array<Intervention>>([]);
const pannesPerProduct = ref<Record<string, Panne[]>>({});
const productTabs = ref<Record<string, string>>({});
const openedPanneIndex = ref<Record<string, number | null>>({});
const pendingPanneIds = ref<Record<string, boolean>>({});
const showProgressBar = ref(false);
const vehicleInventoryItems = ref<VehicleInventoryItem[]>([]);
const pannePhotoBeforeUrls = ref<Record<string, string>>({});
const pannePhotoAfterUrls = ref<Record<string, string>>({});
const pannePhotoBeforeFileIds = ref<Record<string, string>>({});
const pannePhotoAfterFileIds = ref<Record<string, string>>({});
const loadedPanneThumbnailIds = ref<Record<string, boolean>>({});
const isPanneCameraDialogOpen = ref(false);
const panneCameraTarget = ref<{ panneId: string; tag: VaultFileTag } | null>(null);
const isBrowserCameraAvailable = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

const progressInfo = ref<ProgressInfo>({
  fileName: "",
  percentage: 0,
});

const signature = ref(null);
const signatureFile = ref<VaultFile | null>(null);
const signatureUrl = ref(null);

const isInterventionStarted = computed(() => {
  return task.value && task.value.startDateTime ? true : false;
});

const isInterventionEnded = computed(() => {
  return task.value && task.value.endDateTime ? true : false;
});

const productsWithIntervention = computed(() => {
  return products.value.filter((product) =>
    interventions.value.some((intervention) => intervention.productId === product.id)
  );
});

function isSparePartAvailable(sparepartId: string): boolean {
  if (!sparepartId) {
    return false;
  }
  const inventoryItem = vehicleInventoryItems.value.find(
    (item) => item.sparepartId === sparepartId
  );
  return !!inventoryItem && inventoryItem.quantity > 0;
}

function getSparePartLabel(sparepartId: string): string {
  if (!sparepartId) {
    return "Pièce détachée";
  }
  return isSparePartAvailable(sparepartId)
    ? "Pièce détachée (Disponible en véhicule)"
    : "Pièce détachée (Indisponible en véhicule)";
}

onMounted(async () => {
  if (!route.params.id) {
    return;
  }

  task.value = await getTaskById(route.params.id as string);
  task.value.subtasks ||= [];
  laverie.value = await getLaverieById(task.value.laverieId);
  products.value = await getProductsByLaverieId(laverie.value.id);
  interventions.value = await fetchInterventionsByTaskId(task.value.id);
  spareparts.value = await getAllSpareparts();

  products.value.forEach((product) => {
    productTabs.value[product.id] = "intervention";
  });

  vehicle.value = await getVehicleByTaskListId(task.value.taskListId);
  if (vehicle.value) {
    vehicleInventoryItems.value = await getVehicleInventoryItems(vehicle.value.id);
  } else {
    appStore.sendErrorNotice("Véhicule associé à la tâche introuvable. Les pièces détachées ne seront pas affichées.");
  }

  const panneResults = await Promise.all(
    productsWithIntervention.value.map((product) =>
      getPanneByProduct(product.id, false).then((pannes) => ({ productId: product.id, pannes }))
    )
  );

  panneResults.forEach(({ productId, pannes }) => {
    pannesPerProduct.value[productId] = pannes;
  });

  await Promise.all(
    panneResults.flatMap(({ pannes }) =>
      pannes
        .filter((panne) => !!panne.id)
        .map((panne) => loadPanneThumbnails(panne.id as string))
    )
  );

  const files = await fetchVaultFiles({
    refEntityType: RefEntityType.TASK,
    refEntityId: task.value.id,
    type: VaultFileType.SIGNATURE,
  });

  signatureFile.value = files[0] ?? null;
  if (signatureFile.value) {
    await loadSignature(signatureFile.value.id);
  }
});

async function startIntervention() {
  try {
    task.value.startDateTime = new Date();
    await setStartDateTime(task.value.id, task.value.startDateTime);
    interventions.value = await fetchInterventionsByTaskId(task.value.id);
  } catch (e) {
    task.value.startDateTime = null;
  }
}

async function endIntervention() {
  try {
    task.value.endDateTime = new Date();
    await setEndDateTime(task.value.id, task.value.endDateTime);
  } catch (e) {
    task.value.endDateTime = null;
  }
}

function clearSignature() {
  signature.value?.clear?.();
}

async function saveSignature() {
  if (!signature.value || signature.value.isEmpty()) {
    return;
  }

  showProgressBar.value = true;
  progressInfo.value.fileName = "signature.png";
  progressInfo.value.percentage = 0;

  try {
    const response = await uploadTaskSignature(
      signature.value.toDataURL("image/png"),
      task.value.id,
      (event: AxiosProgressEvent) => {
        if (event.progress !== undefined) {
          progressInfo.value.percentage = Math.round(event.progress * 100);
        }
      }
    );
    signatureFile.value = response.data;
    if (signatureFile.value) {
      await loadSignature(signatureFile.value.id);
    }
    appStore.sendSuccessNotice("Signature enregistrée avec succès");
  } catch (e) {
    // Ignore
  } finally {
    showProgressBar.value = false;
  }
}

async function loadSignature(fileId: string) {
  signatureUrl.value = await fetchFileContent(fileId);
}

async function addPanne(product: Product): Promise<void> {
  if (!pannesPerProduct.value[product.id]) {
    pannesPerProduct.value[product.id] = [];
  }

  try {
    const createdPanne = await postPanne(
      {
        name: "",
        desc: "",
        createTask: false,
        installedSpareParts: [],
      },
      product.id
    );

    pannesPerProduct.value[product.id].push(createdPanne);
    if (createdPanne.id) {
      pendingPanneIds.value[createdPanne.id] = true;
    }
    openedPanneIndex.value[product.id] = pannesPerProduct.value[product.id].length - 1;
  } catch (e) {
    // Ignore because it's handled in axios service
  }
}

async function removePanne(product: Product, panneIndex: number): Promise<void> {
  const panne = pannesPerProduct.value[product.id]?.[panneIndex];

  try {
    if (panne?.id) {
      await deletePanne(panne.id);
      delete pendingPanneIds.value[panne.id];
    }

    pannesPerProduct.value[product.id].splice(panneIndex, 1);
    openedPanneIndex.value[product.id] = null;
  } catch (e) {
    // Ignore because it's handled in axios service
  }
}

async function submitPanne(product: Product, panne: Panne): Promise<void> {
  // Ensure the panne has the correct vehicleId before submitting to update the vehicle inventory based on the installed spareparts
  panne.vehicleId = vehicle.value.id;
  try {
    await putPanne(panne);
    if (panne.id) {
      delete pendingPanneIds.value[panne.id];
    }
    openedPanneIndex.value[product.id] = null;
  } catch (e) {
    // Ignore because it's handled in axios service
  }
}

function triggerPanneFileInput(inputId: string): void {
  const input = document.getElementById(inputId) as HTMLInputElement | null;
  input?.click();
}

async function onPanneFileSelected(event: Event, panne: Panne, tag: VaultFileTag): Promise<void> {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (!file) {
    return;
  }

  try {
    const dataUrl = await fileToDataUrl(file);
    await uploadPannePhotoFromDataUrl(dataUrl, panne, tag);
  } finally {
    target.value = "";
  }
}

function openPanneCamera(panne: Panne, tag: VaultFileTag): void {
  if (!panne.id) {
    appStore.sendErrorNotice("Veuillez d'abord enregistrer la panne avant d'ajouter une photo.");
    return;
  }

  panneCameraTarget.value = { panneId: panne.id, tag };
  isPanneCameraDialogOpen.value = true;
}

function closePanneCamera(): void {
  isPanneCameraDialogOpen.value = false;
  panneCameraTarget.value = null;
}

async function onPanneCameraPhotoConfirmed(payload: { image?: string }): Promise<void> {
  const dataUrl = payload?.image;
  if (!dataUrl || !panneCameraTarget.value) {
    return;
  }

  const target = panneCameraTarget.value;
  const panne = findPanneById(target.panneId);
  if (!panne) {
    appStore.sendErrorNotice("Panne introuvable pour l'ajout de photo.");
    return;
  }

  await uploadPannePhotoFromDataUrl(dataUrl, panne, target.tag);
  closePanneCamera();
}

async function uploadPannePhotoFromDataUrl(dataUrl: string, panne: Panne, tag: VaultFileTag): Promise<void> {
  if (!panne.id) {
    appStore.sendErrorNotice("Veuillez d'abord enregistrer la panne avant d'ajouter une photo.");
    return;
  }

  showProgressBar.value = true;
  progressInfo.value.fileName = tag === VaultFileTag.BEFORE ? "photo-avant.png" : "photo-apres.png";
  progressInfo.value.percentage = 0;

  try {
    const response = await uploadPannePhoto(
      dataUrl,
      panne.id,
      (event: AxiosProgressEvent) => {
        if (event.progress !== undefined) {
          progressInfo.value.percentage = Math.round(event.progress * 100);
        }
      },
      tag
    );

    const uploadedFile = response?.data as VaultFile | undefined;
    if (uploadedFile?.id) {
      const thumbnailUrl = await fetchThumbnail(uploadedFile.id);
      setPanneThumbnail(panne.id, tag, thumbnailUrl);
      setPannePhotoFileId(panne.id, tag, uploadedFile.id);
    } else {
      await loadPanneThumbnails(panne.id);
    }

    appStore.sendSuccessNotice("Photo de panne enregistrée avec succès.");
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    showProgressBar.value = false;
  }
}

async function loadPanneThumbnails(panneId: string): Promise<void> {
  try {
    const beforeTaggedFiles = await fetchVaultFiles({
      refEntityType: RefEntityType.PANNE,
      refEntityId: panneId,
      type: VaultFileType.PHOTO,
      tag: VaultFileTag.BEFORE,
    }) as Array<Record<string, unknown>>;

    const afterTaggedFiles = await fetchVaultFiles({
      refEntityType: RefEntityType.PANNE,
      refEntityId: panneId,
      type: VaultFileType.PHOTO,
      tag: VaultFileTag.AFTER,
    }) as Array<Record<string, unknown>>;

    const allFiles = await fetchVaultFiles({
      refEntityType: RefEntityType.PANNE,
      refEntityId: panneId,
      type: VaultFileType.PHOTO,
    }) as Array<Record<string, unknown>>;

    const beforeFile = beforeTaggedFiles[0]
      ?? allFiles.find((file) => readVaultFileTag(file) === VaultFileTag.BEFORE)
      ?? allFiles.find((file) => !readVaultFileTag(file));

    const afterFile = afterTaggedFiles[0]
      ?? allFiles.find((file) => readVaultFileTag(file) === VaultFileTag.AFTER);

    const beforeFileId = readVaultFileId(beforeFile);
    const afterFileId = readVaultFileId(afterFile);

    if (beforeFileId) {
      const beforeThumbnail = await fetchThumbnail(beforeFileId);
      setPanneThumbnail(panneId, VaultFileTag.BEFORE, beforeThumbnail);
      setPannePhotoFileId(panneId, VaultFileTag.BEFORE, beforeFileId);
    }

    if (afterFileId) {
      const afterThumbnail = await fetchThumbnail(afterFileId);
      setPanneThumbnail(panneId, VaultFileTag.AFTER, afterThumbnail);
      setPannePhotoFileId(panneId, VaultFileTag.AFTER, afterFileId);
    }

    loadedPanneThumbnailIds.value[panneId] = true;
  } catch (e) {
    // Ignore because it's handled in axios service
  }
}

async function ensurePanneThumbnailsLoaded(panne: Panne): Promise<void> {
  if (!panne.id || loadedPanneThumbnailIds.value[panne.id]) {
    return;
  }

  await loadPanneThumbnails(panne.id);
}

function readVaultFileId(file?: Record<string, unknown>): string | undefined {
  if (!file) {
    return undefined;
  }

  const rawId = file.id ?? file.fileId;
  return typeof rawId === "string" || typeof rawId === "number" ? String(rawId) : undefined;
}

function readVaultFileTag(file?: Record<string, unknown>): string | undefined {
  if (!file) {
    return undefined;
  }

  const rawTag = file.tag ?? file.fileTag;
  return typeof rawTag === "string" ? rawTag : undefined;
}

function setPanneThumbnail(panneId: string, tag: VaultFileTag, url: string): void {
  if (tag === VaultFileTag.BEFORE) {
    const previousUrl = pannePhotoBeforeUrls.value[panneId];
    if (previousUrl) {
      URL.revokeObjectURL(previousUrl);
    }
    pannePhotoBeforeUrls.value[panneId] = url;
    return;
  }

  const previousUrl = pannePhotoAfterUrls.value[panneId];
  if (previousUrl) {
    URL.revokeObjectURL(previousUrl);
  }
  pannePhotoAfterUrls.value[panneId] = url;
}

function setPannePhotoFileId(panneId: string, tag: VaultFileTag, fileId: string): void {
  if (tag === VaultFileTag.BEFORE) {
    pannePhotoBeforeFileIds.value[panneId] = fileId;
    return;
  }

  pannePhotoAfterFileIds.value[panneId] = fileId;
}

function getPannePhotoFileId(panneId: string, tag: VaultFileTag): string | undefined {
  return tag === VaultFileTag.BEFORE
    ? pannePhotoBeforeFileIds.value[panneId]
    : pannePhotoAfterFileIds.value[panneId];
}

async function openPannePhoto(panneId: string, tag: VaultFileTag): Promise<void> {
  const fileId = getPannePhotoFileId(panneId, tag);
  if (!fileId) {
    appStore.sendErrorNotice("Photo introuvable.");
    return;
  }

  const photoWindow = window.open("", "_blank");
  try {
    const fileUrl = await fetchFileContent(fileId);
    if (photoWindow) {
      photoWindow.location.href = fileUrl;
    } else {
      window.open(fileUrl, "_blank");
    }
  } catch (e) {
    photoWindow?.close();
    appStore.sendErrorNotice("Impossible d'ouvrir la photo.");
  }
}

async function downloadPannePhoto(panneId: string, tag: VaultFileTag): Promise<void> {
  const fileId = getPannePhotoFileId(panneId, tag);
  if (!fileId) {
    appStore.sendErrorNotice("Photo introuvable.");
    return;
  }

  try {
    const fileUrl = await fetchFileContent(fileId);
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = `panne-${panneId}-${tag.toLowerCase()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (e) {
    appStore.sendErrorNotice("Impossible de télécharger la photo.");
  }
}

function findPanneById(panneId: string): Panne | null {
  for (const pannes of Object.values(pannesPerProduct.value)) {
    const foundPanne = pannes.find((item) => item.id === panneId);
    if (foundPanne) {
      return foundPanne;
    }
  }

  return null;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function isPendingPanne(panne: Panne): boolean {
  return !!(panne.id && pendingPanneIds.value[panne.id]);
}

function addInstalledSparepart(panne: Panne): void {
  if (!panne.installedSpareParts) {
    panne.installedSpareParts = [];
  }

  panne.installedSpareParts.push({
    sparepartId: "",
    taskId: task.value.id,
    panneId: panne.id!,
    quantity: 1,
  });
}

function removeSubtask(subtask: Subtask): void {
  if (!task.value.subtasks) {
    task.value.subtasks = [];
  }

  task.value.subtasks = task.value.subtasks.filter((current) => current.id !== subtask.id);
}

function updateSubtask(subtask: Subtask): void {
  if (!task.value.subtasks) {
    task.value.subtasks = [];
  }

  const index = task.value.subtasks.findIndex((current) => current.id === subtask.id);
  if (index >= 0) {
    task.value.subtasks[index] = subtask;
  }
}

const sendEmail = async () => {
  try {
    await sendReport(task.value.id);
    appStore.sendSuccessNotice("Email envoyé avec succès");
  } catch (e) {
    appStore.sendErrorNotice("Erreur lors de l'envoi de l'email");
  }
};

function getIntervention(productId: string): Intervention | undefined {
  return interventions.value.find((i) => i.productId === productId);
}

function isInterventionCompleted(intervention: Intervention): boolean {
  return intervention.items.every((item) => {
    if (item.itemType === InterventionItemType.CHECKBOX) return item.completed;
    if (item.itemType === InterventionItemType.NUMERIC) return item.numericValue != null;
    if (item.itemType === InterventionItemType.TEXT) return item.textValue?.length > 0;
  });
}

async function completeIntervention(intervention: Intervention) {
  try {
    await putIntervention(intervention);
    appStore.sendSuccessNotice("Intervention sur le produit terminée avec succès");
  } catch (e) {
    // Ignore
  }
}

onBeforeUnmount(() => {
  Object.values(pannePhotoBeforeUrls.value).forEach((url) => URL.revokeObjectURL(url));
  Object.values(pannePhotoAfterUrls.value).forEach((url) => URL.revokeObjectURL(url));
});
</script>

<style scoped>
.red-outline-wrapper ::v-deep(.v-field) {
  border-left: 4px solid red !important;
}

.green-outline-wrapper ::v-deep(.v-field) {
  border-left: 4px solid green !important;
}

.panne-thumbnail {
  width: 160px;
  height: 160px;
  cursor: pointer;
}

.panne-thumbnail-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
}

.panne-thumbnail :deep(img) {
  object-fit: contain;
  object-position: center;
}
</style>
